// src/features/executive/export/ExecutivePrintView.tsx
// Print-only header/footer wrapper. CSS in ExecutivePrint.css hides this on
// screen and displays it during window.print(). Renders nothing visible in
// normal flow.

import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { templateImage } from 'constants/theme'
import { getClinicInfo } from 'utils/app.utils'
import type { ExecutiveState } from 'app/slice/executive.slice'
import type { ExecutiveTab } from 'api/executive/executive.types'
import { formatReadableTimestamp } from './format'

interface Props {
  activeTab: ExecutiveTab
  activeTabLabel: string      // already localized
  branchLabel: string
  dateRangeLabel: string
  now: Date
}

export default function ExecutivePrintView({
  activeTab: _activeTab,
  activeTabLabel,
  branchLabel,
  dateRangeLabel,
  now,
}: Props) {
  const { t } = useTranslation()
  const filters = useSelector((s: { executive: ExecutiveState }) => s.executive.filters)
  const branchInfos = getClinicInfo()
  const clinicDisplayName = branchInfos?.clinicName || branchInfos?.clinicNameEn || 'Cliniter Dental'
  const timestamp = formatReadableTimestamp(now)

  // Compose a compact filter summary: drop null/undefined + dateRange/branchId (shown in header).
  const chipParts: string[] = []
  const { dateRange: _dr, branchId: _b, ...scalars } = filters
  Object.entries(scalars).forEach(([k, v]) => {
    if (v != null) chipParts.push(`${k}=${v}`)
  })
  const filterSummary = chipParts.join(' · ')

  return (
    <>
      <div className="executive-print-header">
        <div className="ph-logo-group">
          <img src={branchInfos?.branchLogo || templateImage.logoHorizontal} alt={clinicDisplayName} />
          <strong>{clinicDisplayName}</strong>
        </div>
        <div className="ph-title">{activeTabLabel}</div>
        <div className="ph-meta">
          {dateRangeLabel} | {branchLabel} | {timestamp}
        </div>
      </div>
      {filterSummary && (
        <div className="executive-print-filter-summary">
          {t('EXECUTIVE.EXPORT.EXCEL_FILTERS')}: {filterSummary}
        </div>
      )}
      <div className="executive-print-footer">
        <div>{t('EXECUTIVE.EXPORT.PRINT_FOOTER_LEFT', { clinicName: clinicDisplayName })}</div>
        <div>{t('EXECUTIVE.EXPORT.PRINT_GENERATED', { timestamp })}</div>
        <div>{t('EXECUTIVE.EXPORT.PRINT_PAGE')}</div>
      </div>
    </>
  )
}
